import { cn } from "@/core/lib/utils";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

/**
 * Reusable building blocks for route-level `loading.tsx` skeletons. They mirror
 * the layout of the real pages (same containers, table chrome, and field
 * stacks) so the hand-off from skeleton to content has no visible jump.
 */

/**
 * Stable keys for placeholder lists. The order never changes, so deriving them
 * once keeps React (and Biome's `noArrayIndexKey`) happy without index keys.
 */
export function skeletonKeys(count: number, prefix = "sk"): string[] {
  return Array.from({ length: count }, (_, i) => `${prefix}-${i}`);
}

/** A single text-line shimmer. Heights/widths come from `className`. */
export function SkeletonLine({ className }: { className?: string }) {
  return <Skeleton className={cn("h-4 rounded-md", className)} />;
}

/** Eyebrow + title + description block used at the top of most pages. */
export function PageHeaderSkeleton({
  eyebrow = true,
  className,
}: {
  eyebrow?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      {eyebrow ? <Skeleton className="h-3 w-28 rounded-md" /> : null}
      <Skeleton className="h-7 w-56 rounded-md" />
      <Skeleton className="h-4 w-full max-w-lg rounded-md" />
    </div>
  );
}

/** Bordered table card with a header row and shimmering body rows. */
export function TableSkeleton({
  columns = 4,
  rows = 6,
  className,
}: {
  columns?: number;
  rows?: number;
  className?: string;
}) {
  const columnKeys = skeletonKeys(columns, "col");
  const rowKeys = skeletonKeys(rows, "row");

  return (
    <div className={cn("overflow-hidden rounded-lg border bg-card", className)}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columnKeys.map((key) => (
              <TableHead key={key}>
                <Skeleton className="h-3.5 w-20 rounded-md" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowKeys.map((rowKey) => (
            <TableRow key={rowKey} className="hover:bg-transparent">
              {columnKeys.map((colKey) => (
                <TableCell key={`${rowKey}-${colKey}`}>
                  <Skeleton className="h-4 w-full max-w-35 rounded-md" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Bordered list card whose rows mirror the app's avatar + two-line + trailing
 * badge layout (assets, attachments, assignment lists).
 */
export function ListSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <ul className={cn("divide-y rounded-xl border bg-card", className)}>
      {skeletonKeys(rows, "list").map((key) => (
        <li key={key} className="flex items-center gap-3 p-4">
          <Skeleton className="size-10 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-40 max-w-full rounded-md" />
            <Skeleton className="h-3 w-56 max-w-full rounded-md" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </li>
      ))}
    </ul>
  );
}

/** Stacked label + input field rows, ending in a submit button shimmer. */
export function FormSkeleton({ fields = 5, className }: { fields?: number; className?: string }) {
  return (
    <div className={cn("space-y-6", className)}>
      {skeletonKeys(fields, "field").map((key) => (
        <div key={key} className="space-y-2">
          <Skeleton className="h-3.5 w-24 rounded-md" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      ))}
      <Skeleton className="h-9 w-32 rounded-md" />
    </div>
  );
}

/**
 * Full property detail page placeholder: back button, image + identity header,
 * the three stat cards, and the inventory table. Mirrors `property-detail.tsx`.
 */
export function PropertyDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-36 rounded-md" />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Skeleton className="size-28 shrink-0 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-28 rounded-md" />
          <Skeleton className="h-7 w-48 rounded-md" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {skeletonKeys(3, "stat").map((key) => (
          <div key={key} className="space-y-2 rounded-xl border bg-card p-4 text-center">
            <Skeleton className="mx-auto h-7 w-10 rounded-md" />
            <Skeleton className="mx-auto h-3 w-16 rounded-md" />
          </div>
        ))}
      </div>
      <TableSkeleton columns={5} rows={5} />
    </div>
  );
}

/** A bordered card placeholder, sized by `className`. */
export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 rounded-xl border bg-card p-5", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-20 rounded-md" />
        <Skeleton className="size-7 rounded-md" />
      </div>
      <Skeleton className="h-8 w-16 rounded-md" />
      <Skeleton className="h-3 w-32 rounded-md" />
    </div>
  );
}
